import { Injectable } from '@angular/core';
import { SubmitDocumentsResponse } from '@interfaces/models/doc-types';
import { AvgCostConversion } from '@interfaces/models/spec-id-fallback.enum';
import { S3FileExtensionMap } from '@interfaces/submit/internal-transfer-co0-payload.interface';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { sanitizeError } from '@shared/helpers/sanitize-error-helper';
import { AccountTypes } from '@shared/models';
import { YesNoEmpty } from '@shared/models';
import { LoggerCodes, LoggerService } from '@shared/modules/logger/logger';
import { DocumentsService, FMFService, SubmitService } from '@shared/services';
import { WindowRef } from '@shared/services/window-ref/window-ref.service';
import { getConfigClientFromRTE } from '@src/app/core/config/config-client';
import {
  closeSpinner,
  External,
  HydrateDivorceFromMultiIT,
  HydrateOrgToOrgFromMultiIT,
  LoadClientAccounts,
  LogError,
  LogInfo,
  submitCompleted,
  submitFailed,
  submitInProgress,
  TrackProcessComplete,
  TrackProcessSideStep,
  UpdateInternalTransferAvgCostConversion,
  UpdateInternalTransferMinTaxConversion,
  UpdateInternalTransferTriageOrgToOrg,
  UpdateInternalTransferTriageTransferFrom,
  UpdateInternalTransferTriageTransferTo,
} from '@store/actions';
import { State } from '@store/reducers';
import {
  selectFilesMetadata,
  selectIsWldDocUploadEnabled,
  selectWldDocuments,
} from '@store/selectors/document-upload/document-upload.selectors';
import { selectCO0UploadToggle } from '@store/selectors/feature/feature.selectors';
import { selectFraudDetectorSimulatorLevel } from '@store/selectors/fraud-detector-simulator/fraud-detector-simulator.selectors';
import { selectLetterFilesMetadata } from '@store/selectors/letter-upload/letter-upload.selectors';
import { selectIsSubmitInProgress } from '@store/selectors/submit/submit.selectors';
import {
  catchError,
  filter,
  forkJoin,
  from,
  mergeMap,
  Observable,
  of,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { selectRecaptchaMockResult } from 'vg-interaction-model';

import {
  MultiITSubmit,
  MultiITSummaryContinue,
  SyncMultiITToInternalTransfer,
  TrackMultiITCashSweepTransfer,
  UpdateMultiITTriage,
} from '../actions/multi-internal-transfer.actions';
import { hasAccountData } from '../selectors/multi-internal-transfer.helpers';
import {
  selectAllMultiITToAccounts,
  selectMultiITCashSweepSegments,
  selectMultiITFromAvgCostConversion,
  selectMultiITFromMinTaxConversion,
  selectMultiITFromSelectedAccount,
  selectMultiITFromTransferMethod,
  selectMultiITIsVCOrgToOrg,
  selectMultiITTotalTransferAmount,
  selectMultiITTransferType,
  selectMultiITTriage,
  selectMultiITTriageIsDivorce,
} from '../selectors/multi-internal-transfer.selectors';
import { selectNewAccountRecipients } from '../selectors/open-account/open-account.selectors';
import { selectMultiITCO0Payloads } from '../selectors/submit/multi-it-submit.selectors';

@Injectable()
export class MultiInternalTransferEffects {
  constructor(
    private actions$: Actions,
    private globalStore$: Store<State>,
    private submitService: SubmitService,
    private fmfService: FMFService,
    private documentsService: DocumentsService,
    private loggerService: LoggerService,
    private windowRef: WindowRef,
  ) {}

  buildUrl(isDivorce: boolean): string {
    const config = getConfigClientFromRTE();
    // Strip the current page path to get: https://domain.com/cwp-prefix
    const baseHref = this.windowRef.nativeWindow.location.href
      .split('?')[0]
      .slice(0, -'/internal-transfer/multi-summary'.length);

    if (isDivorce) {
      return baseHref + config.divorce_document_upload_endpoint;
    }

    return baseHref + config.internal_transfer_from_to_endpoint;
  }

  multiITSummaryContinue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MultiITSummaryContinue),
      withLatestFrom(this.globalStore$.pipe(select(selectMultiITTriageIsDivorce))),
      mergeMap(([, isDivorceTransfer]) => {
        const isDivorce = isDivorceTransfer === YesNoEmpty.YES;
        const url = this.buildUrl(isDivorce);
        if (isDivorce) {
          return [LoadClientAccounts(), External({ url })];
        }
        return [External({ url })];
      }),
    ),
  );

  // One tag per distinct qualifying From/To combination rather than only the strongest,
  // so a Cash Plus source split across Cash Plus and VBA recipients fires both.
  fireCashSweepTags$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TrackMultiITCashSweepTransfer),
      withLatestFrom(this.globalStore$.pipe(select(selectMultiITCashSweepSegments))),
      mergeMap(([, segments]) =>
        segments.map((segment) =>
          TrackProcessSideStep({
            processType: 'coo-multi_internal_transfer-flow',
            processSideStep: `coo-multi_internal_transfer-transfer_amounts-cash_sweep_${segment}`,
          }),
        ),
      ),
    ),
  );

  // Sync multi-IT triage state to classic IT state for flows that submit via the single-IT path
  syncSingleITSubmitTriageState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UpdateMultiITTriage),
      withLatestFrom(this.globalStore$.pipe(select(selectMultiITTriage))),
      filter(
        ([, triage]) =>
          triage.isDivorceTransfer === YesNoEmpty.YES ||
          (triage.transferFrom === AccountTypes.ORG &&
            !!triage.transferTo?.includes(AccountTypes.ORG)),
      ),
      mergeMap(([, triage]) => [
        UpdateInternalTransferTriageTransferFrom({ payload: triage.transferFrom }),
        UpdateInternalTransferTriageTransferTo({ payload: triage.transferTo[0] }),
      ]),
    ),
  );

  // One-shot sync of multi-IT state into the classic internal-transfer + cost-basis
  // slices so flows that use the single-IT submit path can submit correctly.
  syncMultiITToInternalTransfer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SyncMultiITToInternalTransfer),
      withLatestFrom(
        this.globalStore$.pipe(select(selectMultiITTriageIsDivorce)),
        this.globalStore$.pipe(select(selectMultiITFromSelectedAccount)),
        this.globalStore$.pipe(select(selectMultiITFromTransferMethod)),
        this.globalStore$.pipe(select(selectMultiITTransferType)),
        this.globalStore$.pipe(select(selectMultiITFromAvgCostConversion)),
        this.globalStore$.pipe(select(selectMultiITFromMinTaxConversion)),
        this.globalStore$.pipe(select(selectMultiITTotalTransferAmount)),
        this.globalStore$.pipe(select(selectAllMultiITToAccounts)),
        this.globalStore$.pipe(select(selectMultiITIsVCOrgToOrg)),
      ),
      filter(
        ([, isDivorce, fromAccount, , , , , , accounts, isVCOrgToOrg]) =>
          (isDivorce === YesNoEmpty.YES || isVCOrgToOrg) && !!fromAccount && accounts.length === 1,
      ),
      mergeMap(
        ([
          ,
          ,
          fromAccount,
          transferMethod,
          transferType,
          avgCostConversion,
          minTaxConversion,
          totalTransferAmount,
          accounts,
          isVCOrgToOrg,
        ]) => {
          const recipient = accounts[0];
          const recipientAccount = recipient.selectedAccount;
          const recipientHasAccountData = hasAccountData(recipientAccount);
          const recipientSelectedAccount =
            recipientHasAccountData && recipientAccount.listIndex != null
              ? { ...recipientAccount, index: recipientAccount.listIndex }
              : recipientAccount;
          const hydratePayload = {
            // Non-null: the filter above already guarantees fromAccount is set,
            // but the boolean predicate doesn't narrow the tuple element type.
            fromAccount: fromAccount!,
            transferMethod,
            transferType,
            avgCostConversion,
            minTaxConversion,
            convertAvgCostHoldings: avgCostConversion === AvgCostConversion.FIFO,
            convertSpecIDHoldings: false,
            transferTo: { selectedAccount: recipientSelectedAccount },
            transactionType: recipient.transactionType,
            percentageOfHoldings: recipient.percentageOfHoldings ?? '',
            totalTransferValue: totalTransferAmount.toString(),
            selectedPositions: recipient.positions ?? [],
          };
          if (isVCOrgToOrg) {
            this.loggerService.info(
              {
                eventId: 'multi-it-vc-to-vc-hydration',
                message: 'Multi-IT VC-to-VC hydration — routing via single-IT submit path',
                data: {
                  isOwner: recipientHasAccountData,
                  transactionType: recipient.transactionType,
                  fromAccountId: fromAccount!.accountData?.accountId,
                  toAccountId: recipientHasAccountData
                    ? recipientAccount.accountData.accountId
                    : undefined,
                },
              },
              LoggerCodes.GENERAL_SUCCESS,
            );
            return [
              HydrateOrgToOrgFromMultiIT({ payload: hydratePayload }),
              UpdateInternalTransferTriageOrgToOrg({
                payload: { isOwner: recipientHasAccountData },
              }),
              UpdateInternalTransferAvgCostConversion({ payload: avgCostConversion }),
              UpdateInternalTransferMinTaxConversion({ payload: minTaxConversion }),
            ];
          }
          return [
            HydrateDivorceFromMultiIT({ payload: hydratePayload }),
            // Reuse the single-flow conversion handlers to flip shownCostBasisMethod
            // to FIFO. Hydrate populates selectedPositions first, then these flip
            // matching positions — the classic submit payload carries min-tax
            // conversion only via shownCostBasisMethod (no boolean field).
            UpdateInternalTransferAvgCostConversion({ payload: avgCostConversion }),
            UpdateInternalTransferMinTaxConversion({ payload: minTaxConversion }),
          ];
        },
      ),
    ),
  );

  multiITSubmit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MultiITSubmit),
      withLatestFrom(
        this.globalStore$.pipe(select(selectIsSubmitInProgress)),
        this.globalStore$.pipe(select(selectMultiITCO0Payloads)),
        this.globalStore$.pipe(select(selectRecaptchaMockResult)),
        this.globalStore$.pipe(select(selectFraudDetectorSimulatorLevel)),
        this.globalStore$.pipe(select(selectMultiITTriageIsDivorce)),
        this.globalStore$.pipe(select(selectFilesMetadata)),
        this.globalStore$.pipe(select(selectLetterFilesMetadata)),
        this.globalStore$.pipe(select(selectCO0UploadToggle)),
        this.globalStore$.pipe(select(selectNewAccountRecipients)),
        this.globalStore$.pipe(select(selectIsWldDocUploadEnabled)),
        this.globalStore$.pipe(select(selectWldDocuments)),
      ),
      filter(([, isSubmitInProgress]) => !isSubmitInProgress),
      tap(() => {
        this.globalStore$.dispatch(submitInProgress());
        this.loggerService.info(
          { eventId: 'multi-it-submit-status', message: 'Multi-IT CO0 Submit in Progress' },
          LoggerCodes.GENERAL_SUCCESS,
        );
      }),
      switchMap(
        ([
          ,
          ,
          payloads,
          mockRecaptchaLevel,
          mockFraudDetectorLevel,
          isDivorce,
          filesMetaData,
          letterFilesMetadata,
          isCO0UploadToggle,
          newAccountRecipients,
          isWldDocUploadEnabled,
          wldDocuments,
        ]) => {
          const isOpeningNewAccount = newAccountRecipients.length > 0;
          let documentSubmit$: Observable<SubmitDocumentsResponse | null | undefined>;
          if (isWldDocUploadEnabled) {
            this.loggerService.info(
              {
                eventId: 'submit-wld-documents',
                message: `Submitting WLD documents: count=${wldDocuments.length}, documentIds=[${wldDocuments.map((d) => d.documentId).join(',')}]`,
              },
              LoggerCodes.GENERAL_SUCCESS,
            );
            documentSubmit$ = from(this.documentsService.submitWldDocuments(wldDocuments));
          } else if (isCO0UploadToggle && isDivorce === YesNoEmpty.YES) {
            documentSubmit$ = from(
              this.documentsService.submitDocuments([...filesMetaData, ...letterFilesMetadata]),
            );
          } else {
            documentSubmit$ = of(null);
          }
          return forkJoin([
            of(payloads),
            this.fmfService.getAllTokens({ recaptcha: 'COO_APPIAN_SUBMIT' }),
            of(mockRecaptchaLevel),
            of(isOpeningNewAccount ? mockFraudDetectorLevel : null),
            documentSubmit$,
          ]);
        },
      ),
      switchMap(
        ([
          payloadsFromSelector,
          fmfResponse,
          mockRecaptchaLevel,
          mockFraudDetectorLevel,
          documentFileName,
        ]) => {
          const payloads = documentFileName?.fileName
            ? payloadsFromSelector.map((payload) => {
                const dotIndex = documentFileName.fileName.lastIndexOf('.');
                const filename =
                  dotIndex > 0
                    ? documentFileName.fileName.substring(0, dotIndex)
                    : documentFileName.fileName;
                const ext =
                  dotIndex > 0
                    ? documentFileName.fileName.substring(dotIndex + 1).toLowerCase()
                    : '';
                return {
                  ...payload,
                  caseInformationFiles: [
                    {
                      filename,
                      fileExtension: S3FileExtensionMap[ext],
                    },
                  ],
                };
              })
            : payloadsFromSelector;

          return this.submitService
            .submitMultiTransactionsEndpoint(
              payloads,
              fmfResponse,
              null,
              mockRecaptchaLevel,
              null,
              mockFraudDetectorLevel,
              '',
            )
            .pipe(
              switchMap((res) => {
                const correlationId = res.correlationId || res.transactId || '';

                if (!correlationId) {
                  throw new Error('Multi-IT CO0 submit returned empty correlationId');
                }

                return [
                  LogInfo({
                    payload: {
                      eventId: 'multi-it-submit-success',
                      message: 'Multi-IT CO0 Submit Success',
                    },
                  }),
                  TrackProcessComplete({
                    processType: 'coo-internal_transfer-flow',
                    accountType: 'MULTI_IT',
                    transactionId: correlationId,
                  }),
                  submitCompleted(),
                ];
              }),
              catchError((e) => [
                submitFailed(),
                closeSpinner(),
                LogError({
                  payload: {
                    eventId: 'multi-it-submit-error',
                    message: `Multi-IT CO0 Submit Error: ${JSON.stringify(sanitizeError(e))}`,
                    code: LoggerCodes.NULL_FAILURE,
                  },
                }),
              ]),
            );
        },
      ),
      catchError((e) => [
        submitFailed(),
        closeSpinner(),
        LogError({
          payload: {
            eventId: 'multi-it-submit-effects-error',
            message: `Multi-IT Submit Effects Error: ${JSON.stringify(sanitizeError(e))}`,
            code: LoggerCodes.NULL_FAILURE,
          },
        }),
      ]),
    ),
  );
}
